import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import { toast } from "react-hot-toast";
import ReactGA from "react-ga4";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Seo from "../../components/Seo";
import LoginRegisterModal from "../../components/LoginRegisterModal/LoginRegisterModal";
import ProductSchemaMarkup from "./ProductSchemaMarkup";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  useMediaQuery,
  Rating,
  Divider,
  Stack,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import AppleIcon from "@mui/icons-material/Apple";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx";

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function formatPrice(value) {
  return Number(value || 0).toFixed(2);
}

function scoreRelatedProduct(candidate, currentProduct) {
  let score = 0;

  const candidateFields = [
    candidate?.category,
    candidate?.catalogCategory,
    candidate?.productCategory,
    candidate?.productType,
    candidate?.name,
    candidate?.shortDescription,
    candidate?.cardSummary,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const sharedExactFields = [
    [candidate?.category, currentProduct?.category, 6],
    [candidate?.catalogCategory, currentProduct?.catalogCategory, 5],
    [candidate?.productCategory, currentProduct?.productCategory, 5],
    [candidate?.productType, currentProduct?.productType, 4],
  ];

  sharedExactFields.forEach(([candidateValue, currentValue, weight]) => {
    if (
      normalizeText(candidateValue) &&
      normalizeText(candidateValue) === normalizeText(currentValue)
    ) {
      score += weight;
    }
  });

  const currentTokens = [
    currentProduct?.category,
    currentProduct?.catalogCategory,
    currentProduct?.productCategory,
    currentProduct?.productType,
    currentProduct?.name,
  ]
    .filter(Boolean)
    .flatMap((value) => normalizeText(value).split(/[^a-z0-9]+/))
    .filter((token) => token.length > 2);

  const tokenMatches = new Set(
    currentTokens.filter((token) => candidateFields.includes(token))
  ).size;
  score += tokenMatches * 0.8;

  const candidateIsFree = Number(candidate?.price || 0) <= 0;
  const currentIsFree = Number(currentProduct?.price || 0) <= 0;
  if (candidateIsFree === currentIsFree) {
    score += 0.0;
  }

  const featuredOrder = Number(candidate?.featuredOrder);
  if (Number.isFinite(featuredOrder)) {
    score += Math.max(0, 3 - featuredOrder * 0.1);
  }

  score += Number(candidate?.starRating || 0) * 0.35;
  score += Math.min(Number(candidate?.ratingCount || 0), 20) * 0.08;

  return score;
}

function SuggestedProductCard({ product, style, getProductDisplayPricing }) {
  if (!product) return null;

  const isFree = Number(product.price || 0) <= 0;
  const pricing = !isFree ? getProductDisplayPricing(product) : null;
  const hasPromoDiscount = Boolean(pricing?.hasDiscount);
  const finalPrice = Number(pricing?.finalPrice ?? product.price ?? 0);
  const originalPrice = Number(pricing?.originalPrice ?? product.price ?? 0);
  const comparisonPrice = hasPromoDiscount
    ? originalPrice
    : Number(product.msrp || 0) > finalPrice
      ? Number(product.msrp)
      : null;

  const mainImage = product.images?.find((image) => image?.URI);

  return (
    <Paper
      component={RouterLink}
      to={`/products/${product.productURI}`}
      elevation={0}
      sx={{
        textDecoration: "none",
        width: "100%",
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "15px",
        backgroundColor: style.color1,
        border: `1px solid ${style.color2}`,
        boxShadow: "0 8px 22px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.025)",
        transition:
          "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: { sm: "translateY(-3px)" },
          borderColor: "rgba(125,165,215,0.42)",
          boxShadow:
            "0 14px 30px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04)",
        },
      }}
    >
      <Box sx={{ p: 1.1 }}>
        <Box
          sx={{
            height: 170,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "11px",
            backgroundColor: style.darkerPanel,
            border: "1px solid rgba(255,255,255,0.10)",
            overflow: "hidden",
          }}
        >
          {mainImage?.URI ? (
            <Box
              component="img"
              src={mainImage.URI}
              alt={mainImage.altText || product.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                p: 1,
              }}
            />
          ) : (
            <Typography sx={{ color: style.color3, fontSize: "0.82rem" }}>
              Image unavailable
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          px: 1.1,
          pb: 1.1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: style.color4,
              fontSize: "1rem",
              fontWeight: 800,
              lineHeight: 1.2,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.name}
          </Typography>

          <Box sx={{ minHeight: 20 }}>
            {Number(product.ratingCount || 0) > 0 ? (
              <Stack
                direction="row"
                spacing={0.65}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Rating
                  value={Number(product.starRating || 0)}
                  precision={0.5}
                  readOnly
                  size="small"
                />
                <Typography
                  sx={{ color: style.color3, fontSize: "0.78rem", fontWeight: 700 }}
                >
                  {product.starRating} ({product.ratingCount})
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ color: style.color3, fontSize: "0.78rem" }}>
                Explore this plugin
              </Typography>
            )}
          </Box>

          <Typography
            sx={{
              color: style.color3,
              fontSize: "0.86rem",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.cardSummary || product.shortDescription}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1.15, backgroundColor: "rgba(255,255,255,0.08)" }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.25}
        >
          {isFree ? (
            <Typography
              sx={{ color: "#20d45b", fontSize: "1.2rem", fontWeight: 900 }}
            >
              FREE
            </Typography>
          ) : (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
            >
              {comparisonPrice && comparisonPrice > finalPrice && (
                <Typography
                  sx={{
                    color: style.color3,
                    textDecoration: "line-through",
                    fontSize: "0.88rem",
                  }}
                >
                  ${formatPrice(comparisonPrice)}
                </Typography>
              )}

              <Typography
                sx={{ color: style.color4, fontSize: "1.18rem", fontWeight: 900 }}
              >
                ${formatPrice(finalPrice)}
              </Typography>
            </Stack>
          )}

          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.45,
              color: style.color4,
              fontSize: "0.84rem",
              fontWeight: 800,
            }}
          >
            View
            <ArrowForwardRoundedIcon sx={{ fontSize: "1rem" }} />
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function ProductPage() {
  const { user } = useContext(UserContext);
  const { style } = useStyle();
  const { addToCart } = useContext(ShoppingCartContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { sku } = useParams();
  const navigate = useNavigate();

  const [productData, setProductData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
  const [macDownloadRequested, setMacDownloadRequested] = useState(false);
  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [expandedControls, setExpandedControls] = useState({});
  const [isImagePreviewOpen, setImagePreviewOpen] = useState(false);
  const { getProductDisplayPricing } = usePromotions();
  const productPricing = useMemo(() => {
    if (!productData || Number(productData.price) <= 0) return null;
    return getProductDisplayPricing(productData);
  }, [productData, getProductDisplayPricing]);

  const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
  const originalPrice = Number(productPricing?.originalPrice ?? productData?.price ?? 0);
  const finalPrice = Number(productPricing?.finalPrice ?? productData?.price ?? 0);
  const savingsPercent =
    hasPromoDiscount && originalPrice > 0
      ? Math.round((Number(productPricing.discountAmount) / originalPrice) * 100)
      : 0;

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: sku, title: sku });
    getProductPageInfo();
  }, [sku]);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSelectedImageIndex(0);
    setExpandedControls({});
    setImagePreviewOpen(false);
  }, [productData?.productURI]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [sku]);

  const bulletPoints = useMemo(() => {
    if (!productData) return [];
    return [
      productData.bulletPoint1,
      productData.bulletPoint2,
      productData.bulletPoint3,
      productData.bulletPoint4,
      productData.bulletPoint5,
    ].filter(Boolean);
  }, [productData]);

  const galleryImages = useMemo(() => {
    if (!productData?.images) return [];
    const valid = productData.images.filter((img) => img && img.URI);
    if (valid.length <= 1) return valid;
    return valid.slice(0, -1);
  }, [productData]);

  const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

  const youtubeEmbedUrl = useMemo(() => {
    const rawValue = productData?.youtubeVideo?.trim();
    if (!rawValue) return "";

    try {
      const url = new URL(rawValue);
      const hostname = url.hostname.replace(/^www\./, "");

      if (hostname === "youtu.be") {
        const videoId = url.pathname.split("/").filter(Boolean)[0];
        return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
      }

      if (hostname.endsWith("youtube.com")) {
        if (url.pathname.startsWith("/embed/")) {
          return rawValue;
        }

        const pathSegments = url.pathname.split("/").filter(Boolean);
        const videoId =
          url.searchParams.get("v") ||
          (pathSegments[0] === "shorts" ? pathSegments[1] : "");

        if (!videoId) return "";

        const startAt =
          url.searchParams.get("start") || url.searchParams.get("t");
        const startSeconds = startAt ? startAt.replace(/s$/i, "") : "";

        return `https://www.youtube.com/embed/${videoId}${
          startSeconds ? `?start=${startSeconds}` : ""
        }`;
      }

      return rawValue;
    } catch (error) {
      return `https://www.youtube.com/embed/${rawValue}`;
    }
  }, [productData?.youtubeVideo]);

  const relatedProducts = useMemo(() => {
    if (!productData || !allProducts.length) return [];
  
    return allProducts
      .filter(
        (candidate) =>
          candidate?.productURI &&
          candidate.productURI !== productData.productURI &&
          Number(candidate?.price || 0) > 0
      )
      .map((candidate) => ({
        product: candidate,
        score: scoreRelatedProduct(candidate, productData),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((entry) => entry.product);
  }, [allProducts, productData]);

  const closeModal = (setModalFn) => () => setModalFn(false);

  const getProductPageInfo = async () => {
    try {
      const [productResponse, allProductsResponse] = await Promise.all([
        axios.post("/get_product_page_info", { productURI: sku }),
        axios.post("/api/get-products-available-on-website"),
      ]);

      if (productResponse?.data) {
        setProductData(productResponse.data);
      }

      setAllProducts(
        Array.isArray(allProductsResponse?.data) ? allProductsResponse.data : []
      );
    } catch (error) {
      console.log("Error retrieving data...", error);
    }
  };

  const addProductToCart = (e) => {
    e.preventDefault();
    addToCart(productData.SKU);
  };

  const downloadWindowsVST = async () => {
    const token = localStorage.getItem("token");

    if (!user) {
      setWindowsDownloadRequested(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      window.location.href = productData.windowsDownloadURI;
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const downloadMacVST = async () => {
    const token = localStorage.getItem("token");

    if (!user) {
      setMacDownloadRequested(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      window.location.href = productData.macDownloadURI;
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const navToSubscribePage = () => {
    navigate("/subscribe");
  };

  const loginCallback = async () => {
    const token = localStorage.getItem("token");
    if (!token || !productData) return;

    setLoginRegisterModalOpen(false);

    try {
      if (windowsDownloadRequested) {
        setWindowsDownloadRequested(false);
        await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });
        window.location.href = productData.windowsDownloadURI;
      }

      if (macDownloadRequested) {
        setMacDownloadRequested(false);
        await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });
        window.location.href = productData.macDownloadURI;
      }
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const nextImage = () => {
    if (galleryImages.length < 2) return;
    setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const previousImage = () => {
    if (galleryImages.length < 2) return;
    setSelectedImageIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );
  };

  const toggleControlExpanded = (controlIndex) => {
    setExpandedControls((previousState) => ({
      ...previousState,
      [controlIndex]: !previousState[controlIndex],
    }));
  };

  if (!productData) {
    return (
      <>
        <Seo
          title="Parish Audio Plugin"
          description="Professional audio plugins by Parish Audio."
          canonical="https://parishaudio.com/products"
        />
        <Box sx={{ minHeight: height - 130 }}>
          <LoadingSpinner />
        </Box>
      </>
    );
  }

  const panelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  const videoDemoSection = youtubeEmbedUrl ? (
    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlinedIcon sx={{ color: style.color3 }} />
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
            Product Demo
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "56.25%",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "rgba(0,0,0,0.18)",
          }}
        >
          <Box
            component="iframe"
            src={youtubeEmbedUrl}
            title={`${productData.name} product demo`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        </Box>
      </Stack>
    </Paper>
  ) : null;

  return (
    <>
      <Seo
        title={`${productData.name} VST3 Plugin | Parish Audio`}
        description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
        canonical={`https://parishaudio.com/products/${productData.productURI}`}
        image={
          productData?.images?.[0]?.URI
            ? `https://parishaudio.com${productData.images[0].URI}`
            : "https://parishaudio.com/assets/og-default.jpg"
        }
        imageAlt={
          productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
        }
        ogType="product"
      />

      <ProductSchemaMarkup
        productData={productData}
        productPricing={productPricing}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <SaleHeroBanner />
          <br />
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              {isMobile ? (
                <Stack spacing={1.5}>
                  <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
                    <Box
                      sx={{
                        minHeight: 260,
                        height: 260,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.18)",
                        borderRadius: "12px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {galleryImages.length > 1 && (
                        <>
                          <IconButton
                            onClick={previousImage}
                            sx={{
                              position: "absolute",
                              left: 8,
                              zIndex: 2,
                              backgroundColor: "rgba(0,0,0,0.32)",
                              color: style.color4,
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                            }}
                          >
                            <ArrowBackIosNewRoundedIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            onClick={nextImage}
                            sx={{
                              position: "absolute",
                              right: 8,
                              zIndex: 2,
                              backgroundColor: "rgba(0,0,0,0.32)",
                              color: style.color4,
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                            }}
                          >
                            <ArrowForwardIosRoundedIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}

                      <Box
                        component="img"
                        src={selectedImage?.URI}
                        alt={selectedImage?.altText || productData.name}
                        onClick={() => setImagePreviewOpen(true)}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          display: "block",
                          p: 1,
                          transition: "transform 180ms ease",
                          "&:hover": {
                            transform: { sm: "scale(1.025)" },
                          },
                        }}
                      />
                    </Box>

                    {galleryImages.length > 1 && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "grid",
                          gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
                          gap: 1,
                        }}
                      >
                        {galleryImages.map((image, index) => (
                          <Box
                            key={`${image.URI}-${index}`}
                            onClick={() => setSelectedImageIndex(index)}
                            sx={{
                              height: 74,
                              cursor: "pointer",
                              borderRadius: "10px",
                              overflow: "hidden",
                              border:
                                index === selectedImageIndex
                                  ? `2px solid ${style.color2}`
                                  : "2px solid transparent",
                              backgroundColor:
                                index === selectedImageIndex
                                  ? "rgba(255,255,255,0.06)"
                                  : "rgba(0,0,0,0.18)",
                              "&:hover": { borderColor: style.color3 },
                            }}
                          >
                            <Box
                              component="img"
                              src={image.URI}
                              alt={image.altText || `${productData.name} preview ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                                p: 0.75,
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
                    <Stack spacing={1}>
                      <Chip
                        label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
                        sx={{
                          alignSelf: "flex-start",
                          borderRadius: "5px",
                          color: style.color4,
                          fontWeight: 700,
                          backgroundColor:
                            productData.price > 0
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,180,60,0.18)",
                          border:
                            productData.price > 0
                              ? "1px solid rgba(255,255,255,0.08)"
                              : "1px solid rgba(0,200,80,0.35)",
                        }}
                      />

                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: { xs: "2rem", md: "2.7rem" },
                          lineHeight: 1.04,
                          fontWeight: 800,
                          color: style.color4,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        {productData.name}
                      </Typography>

                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "1rem",
                          lineHeight: 1.7,
                        }}
                      >
                        {productData.shortDescription}
                      </Typography>

                      <Box
                        component={RouterLink}
                        to={`/reviews/${productData.productURI}`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          width: "fit-content",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <Rating
                          value={productData.starRating || 0}
                          precision={0.5}
                          readOnly
                        />
                        <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                          {productData.ratingCount > 0 ? productData.starRating : ""}
                        </Typography>
                        <Typography sx={{ color: style.color3 }}>
                          ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Chip
                        label="IN STOCK • INSTANT DOWNLOAD"
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          color: style.color4,
                          backgroundColor: "rgba(0,180,60,0.18)",
                          border: "1px solid rgba(0,200,80,0.35)",
                          fontWeight: 700,
                          letterSpacing: 0.2,
                        }}
                      />

                      {productData.price > 0 ? (
                        <>
                          <Typography sx={{ color: style.color3, fontWeight: 700 }}>
                            Purchase this plugin
                          </Typography>

                          <Stack spacing={0.8}>
                            {hasPromoDiscount && (
                              <Chip
                                label={`SALE - Save ${savingsPercent}%`}
                                size="small"
                                sx={{
                                  alignSelf: "flex-start",
                                  color: "#111",
                                  backgroundColor: "#ffcc4d",
                                  border: "1px solid rgba(255,255,255,0.18)",
                                  fontWeight: 900,
                                  letterSpacing: 0.2,
                                }}
                              />
                            )}

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="baseline"
                              flexWrap="wrap"
                            >
                              <Typography
                                sx={{
                                  color: style.color3,
                                  textDecoration: "line-through",
                                  fontSize: "1.05rem",
                                }}
                              >
                                ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: "2rem",
                                  fontWeight: 900,
                                  color: style.color4,
                                  lineHeight: 1,
                                }}
                              >
                                ${finalPrice.toFixed(2)}
                              </Typography>
                            </Stack>

                            {hasPromoDiscount && (
                              <Typography
                                sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}
                              >
                                Sale price applied automatically at checkout.
                              </Typography>
                            )}
                          </Stack>

                          <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                            One-time purchase. Add this plugin to your cart and own it permanently.
                          </Typography>

                          <Button
                            onClick={addProductToCart}
                            startIcon={<ShoppingCartRoundedIcon />}
                            fullWidth
                            sx={{
                              minHeight: 50,
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 800,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                            }}
                          >
                            Add to Cart
                          </Button>

                          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
                              <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                                Subscription
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                              Try it free for 7 days with an all access subscription.
                            </Typography>

                            <Button
                              onClick={navToSubscribePage}
                              startIcon={<BoltRoundedIcon />}
                              fullWidth
                              sx={{
                                minHeight: 46,
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Try FREE for 7 Days
                            </Button>
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: "2rem",
                              color: "rgb(30,170,60)",
                              lineHeight: 1,
                            }}
                          >
                            FREE
                          </Typography>

                          <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                            Download this VST3 plugin for Mac or Windows.
                          </Typography>

                          <Stack spacing={1}>
                            {productData.windowsDownloadURI && (
                              <Button
                                onClick={downloadWindowsVST}
                                startIcon={<WindowsRoundedIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Download for Windows
                              </Button>
                            )}

                            {productData.macDownloadURI && (
                              <Button
                                onClick={downloadMacVST}
                                startIcon={<AppleIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Download for Mac
                              </Button>
                            )}
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>

                  {productData.audioURIs?.[0] && (
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
                          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                            Audio Demo
                          </Typography>
                        </Stack>
                        <audio controls style={{ width: "100%", height: "36px" }}>
                          <source src={productData.audioURIs[0]} type="audio/mpeg" />
                        </audio>
                      </Stack>
                    </Paper>
                  )}

                  {videoDemoSection}

                  {productData.price > 0 && (
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DownloadRoundedIcon sx={{ color: style.color3 }} />
                          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                            Download Information
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                          Availability: In stock. This product is delivered as an instant digital download.
                        </Typography>

                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                              <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                Download the plugin installer for your device here.
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                              <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                              <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
                              </Typography>
                            </Stack>
                          </Stack>
                        </Paper>

                        <Stack spacing={1}>
                          {productData.windowsDownloadURI && (
                            <Button
                              component="a"
                              href={productData.windowsDownloadURI}
                              startIcon={<WindowsRoundedIcon />}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Windows Download
                            </Button>
                          )}

                          {productData.macDownloadURI && (
                            <Button
                              component="a"
                              href={productData.macDownloadURI}
                              startIcon={<AppleIcon />}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Mac Download
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(360px, 0.92fr) minmax(0, 1.08fr)",
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Box
                        sx={{
                          minHeight: 420,
                          height: 420,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(0,0,0,0.18)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {galleryImages.length > 1 && (
                          <>
                            <IconButton
                              onClick={previousImage}
                              sx={{
                                position: "absolute",
                                left: 10,
                                zIndex: 2,
                                backgroundColor: "rgba(0,0,0,0.32)",
                                color: style.color4,
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                              }}
                            >
                              <ArrowBackIosNewRoundedIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              onClick={nextImage}
                              sx={{
                                position: "absolute",
                                right: 10,
                                zIndex: 2,
                                backgroundColor: "rgba(0,0,0,0.32)",
                                color: style.color4,
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                              }}
                            >
                              <ArrowForwardIosRoundedIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}

                        <Box
                          component="img"
                          src={selectedImage?.URI}
                          alt={selectedImage?.altText || productData.name}
                          onClick={() => setImagePreviewOpen(true)}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block",
                            p: 1.5,
                            transition: "transform 180ms ease",
                            "&:hover": {
                              transform: "scale(1.025)",
                            },
                          }}
                        />
                      </Box>

                      {galleryImages.length > 1 && (
                        <Box
                          sx={{
                            mt: 1.25,
                            display: "grid",
                            gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
                            gap: 1,
                          }}
                        >
                          {galleryImages.map((image, index) => (
                            <Box
                              key={`${image.URI}-${index}`}
                              onClick={() => setSelectedImageIndex(index)}
                              sx={{
                                height: 82,
                                cursor: "pointer",
                                borderRadius: "10px",
                                overflow: "hidden",
                                border:
                                  index === selectedImageIndex
                                    ? `2px solid ${style.color2}`
                                    : "2px solid transparent",
                                backgroundColor:
                                  index === selectedImageIndex
                                    ? "rgba(255,255,255,0.06)"
                                    : "rgba(0,0,0,0.18)",
                                "&:hover": { borderColor: style.color3 },
                              }}
                            >
                              <Box
                                component="img"
                                src={image.URI}
                                alt={image.altText || `${productData.name} preview ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  display: "block",
                                  p: 0.75,
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>

                    {productData.audioURIs?.[0] && (
                      <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
                            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                              Audio Demo
                            </Typography>
                          </Stack>
                          <audio controls style={{ width: "100%", height: "36px" }}>
                            <source src={productData.audioURIs[0]} type="audio/mpeg" />
                          </audio>
                        </Stack>
                      </Paper>
                    )}

                    {videoDemoSection}

                    {productData.price > 0 && (
                      <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <DownloadRoundedIcon sx={{ color: style.color3 }} />
                            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                              Download Information
                            </Typography>
                          </Stack>

                          <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                            Availability: In stock. This product is delivered as an instant digital download.
                          </Typography>

                          <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  Download the plugin installer for your device here.
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
                                </Typography>
                              </Stack>
                            </Stack>
                          </Paper>

                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                            {productData.windowsDownloadURI && (
                              <Button
                                component="a"
                                href={productData.windowsDownloadURI}
                                startIcon={<WindowsRoundedIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                  },
                                  flex: 1,
                                }}
                              >
                                Windows Download
                              </Button>
                            )}

                            {productData.macDownloadURI && (
                              <Button
                                component="a"
                                href={productData.macDownloadURI}
                                startIcon={<AppleIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                  },
                                  flex: 1,
                                }}
                              >
                                Mac Download
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>

                  <Stack spacing={1.5}>
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
                      <Stack spacing={1}>
                        <Chip
                          label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
                          sx={{
                            alignSelf: "flex-start",
                            borderRadius: "5px",
                            color: style.color4,
                            fontWeight: 700,
                            backgroundColor:
                              productData.price > 0
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(0,180,60,0.18)",
                            border:
                              productData.price > 0
                                ? "1px solid rgba(255,255,255,0.08)"
                                : "1px solid rgba(0,200,80,0.35)",
                          }}
                        />

                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: { xs: "2rem", md: "2.65rem" },
                            lineHeight: 1.04,
                            fontWeight: 800,
                            color: style.color4,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {productData.name}
                        </Typography>

                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: { xs: "1rem", md: "1.03rem" },
                            lineHeight: 1.7,
                          }}
                        >
                          {productData.shortDescription}
                        </Typography>

                        <Box
                          component={RouterLink}
                          to={`/reviews/${productData.productURI}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            width: "fit-content",
                            textDecoration: "none",
                            color: "inherit",
                            pt: 0.25,
                          }}
                        >
                          <Rating
                            value={productData.starRating || 0}
                            precision={0.5}
                            readOnly
                          />
                          <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                            {productData.ratingCount > 0 ? productData.starRating : ""}
                          </Typography>
                          <Typography sx={{ color: style.color3 }}>
                            ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider sx={{ my: 1.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

                      <Stack spacing={1.5}>
                        <Chip
                          label="IN STOCK • INSTANT DOWNLOAD"
                          size="small"
                          sx={{
                            alignSelf: "flex-start",
                            color: style.color4,
                            backgroundColor: "rgba(0,180,60,0.18)",
                            border: "1px solid rgba(0,200,80,0.35)",
                            fontWeight: 700,
                            letterSpacing: 0.2,
                          }}
                        />

                        {productData.price > 0 ? (
                          <>
                            <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                              Purchase this plugin
                            </Typography>
                            <Stack spacing={0.8}>
                              {hasPromoDiscount && (
                                <Chip
                                  label={`SALE - Save ${savingsPercent}%`}
                                  size="small"
                                  sx={{
                                    alignSelf: "flex-start",
                                    color: "#111",
                                    backgroundColor: "#ffcc4d",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    fontWeight: 900,
                                    letterSpacing: 0.2,
                                  }}
                                />
                              )}

                              <Stack
                                direction="row"
                                spacing={1.25}
                                alignItems="baseline"
                                flexWrap="wrap"
                              >
                                <Typography
                                  sx={{
                                    color: style.color3,
                                    textDecoration: "line-through",
                                    fontSize: "1.05rem",
                                  }}
                                >
                                  ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: "2rem",
                                    fontWeight: 900,
                                    color: style.color4,
                                    lineHeight: 1,
                                  }}
                                >
                                  ${finalPrice.toFixed(2)}
                                </Typography>
                              </Stack>

                              {hasPromoDiscount && (
                                <Typography
                                  sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}
                                >
                                  Sale price applied automatically at checkout.
                                </Typography>
                              )}
                            </Stack>

                            <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                              One-time purchase. Add this plugin to your cart and own it permanently.
                            </Typography>

                            <Button
                              onClick={addProductToCart}
                              startIcon={<ShoppingCartRoundedIcon />}
                              fullWidth
                              sx={{
                                minHeight: 52,
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 800,
                                color: style.color4,
                                backgroundColor: style.accentGreen,
                                px: 2.5,
                                "&:hover": {
                                  backgroundColor: style.accentGreen,
                                  filter: "brightness(1.05)",
                                },
                              }}
                            >
                              Add to Cart
                            </Button>

                            <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                            <Stack spacing={0.8}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
                                <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                                  Subscription
                                </Typography>
                              </Stack>

                              <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                                Try it free for 7 days with an all access subscription.
                              </Typography>

                              <Button
                                onClick={navToSubscribePage}
                                startIcon={<BoltRoundedIcon />}
                                fullWidth
                                sx={{
                                  minHeight: 46,
                                  alignSelf: "stretch",
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  px: 2.5,
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                  },
                                }}
                              >
                                Try FREE for 7 Days
                              </Button>

                              <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                                $9.95 per month for access to all plugins. Cancel anytime.
                              </Typography>
                            </Stack>
                          </>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                fontWeight: 900,
                                fontSize: "2.2rem",
                                color: "rgb(30,170,60)",
                                lineHeight: 1,
                              }}
                            >
                              FREE
                            </Typography>

                            <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                              Download this VST3 plugin for Mac or Windows.
                            </Typography>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                              {productData.windowsDownloadURI && (
                                <Button
                                  onClick={downloadWindowsVST}
                                  startIcon={<WindowsRoundedIcon />}
                                  sx={{
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: style.color4,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.10)",
                                      filter: "brightness(1.05)",
                                    },
                                  }}
                                >
                                  Download for Windows
                                </Button>
                              )}

                              {productData.macDownloadURI && (
                                <Button
                                  onClick={downloadMacVST}
                                  startIcon={<AppleIcon />}
                                  sx={{
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: style.color4,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.10)",
                                      filter: "brightness(1.05)",
                                    },
                                  }}
                                >
                                  Download for Mac
                                </Button>
                              )}
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LibraryMusicRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
                          Overview
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          color: style.color4,
                          lineHeight: 1.75,
                          whiteSpace: "pre-line",
                          fontSize: "1rem",
                        }}
                      >
                        {productData.longDescription}
                      </Typography>

                      {bulletPoints.length > 0 && (
                        <>
                          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <InfoOutlinedIcon sx={{ color: style.color3 }} />
                              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                                Highlights
                              </Typography>
                            </Stack>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 1.1,
                              }}
                            >
                              {bulletPoints.map((point, idx) => (
                                <Paper
                                  key={idx}
                                  elevation={0}
                                  sx={{
                                    ...subtlePanelSx,
                                    p: 1.15,
                                    minHeight: 25,
                                    display: "flex",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircleRoundedIcon
                                      sx={{
                                        color: style.color3,
                                        mt: "2px",
                                        fontSize: "1.05rem",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        color: style.color4,
                                        lineHeight: 1.5,
                                        fontSize: "0.92rem",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {point}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              ))}
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TuneRoundedIcon sx={{ color: style.color3 }} />
                          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
                            Controls
                          </Typography>
                        </Stack>

                        {productData.controls?.length > 0 && (
                          <Chip
                            label={`${productData.controls.length} control${productData.controls.length === 1 ? "" : "s"}`}
                            size="small"
                            sx={{
                              color: style.color4,
                              borderRadius: "6px",
                              backgroundColor: "rgba(255,255,255,0.06)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              fontWeight: 800,
                            }}
                          />
                        )}
                      </Stack>

                      {productData.controls?.length > 0 ? (
                        <Stack spacing={1}>
                          {productData.controls.map((control, index) => (
                            <Accordion
                              key={`${control?.name || "control"}-${index}`}
                              expanded={Boolean(expandedControls[index])}
                              onChange={() => toggleControlExpanded(index)}
                              disableGutters
                              elevation={0}
                              sx={{
                                backgroundColor: "rgba(0,0,0,0.18)",
                                color: style.color4,
                                borderRadius: "12px !important",
                                border: "1px solid rgba(255,255,255,0.06)",
                                boxShadow: "none",
                                overflow: "hidden",
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreRoundedIcon sx={{ color: style.color4 }} />}
                                sx={{
                                  px: 1.5,
                                  minHeight: 56,
                                  "& .MuiAccordionSummary-content": {
                                    my: 1.05,
                                  },
                                }}
                              >
                                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      color: style.color4,
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {control.name}
                                  </Typography>

                                  {!expandedControls[index] && (
                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        lineHeight: 1.5,
                                        fontSize: "0.9rem",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {control.description}
                                    </Typography>
                                  )}
                                </Stack>
                              </AccordionSummary>

                              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.4 }}>
                                <Typography
                                  sx={{
                                    color: style.color3,
                                    lineHeight: 1.6,
                                    fontSize: "0.92rem",
                                  }}
                                >
                                  {control.description}
                                </Typography>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ color: style.color3 }}>
                          No control information available.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              </Box>

              {relatedProducts.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

                  <Stack spacing={1.2}>
                    <Box>
                      <Typography
                        sx={{ color: style.color4, fontSize: "1.25rem", fontWeight: 800 }}
                      >
                        Suggested Plugins
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.6, mt: 0.35 }}>
                        Explore a few more Parish Audio plugins that you might like.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                        gap: 1.5,
                      }}
                    >
                      {relatedProducts.map((product) => (
                        <SuggestedProductCard
                          key={product.SKU || product.productURI}
                          product={product}
                          style={style}
                          getProductDisplayPricing={getProductDisplayPricing}
                        />
                      ))}
                    </Box>
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Box sx={{ pt: 0.5, pb: 0.85 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                    Format:
                  </Typography>
                  <Chip
                    label="VST3"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                  <Chip
                    label="Mac"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                  <Chip
                    label="Windows"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    component={RouterLink}
                    to={`/reviews/${productData.productURI}`}
                    variant="text"
                    startIcon={<InfoOutlinedIcon />}
                    sx={{
                      color: style.color4,
                      fontWeight: 700,
                      px: 1.35,
                      py: 0.7,
                      borderRadius: "999px",
                      textTransform: "none",
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.10)",
                        color: style.color4,
                      },
                    }}
                  >
                    Read all reviews
                  </Button>

                  <Button
                    component={RouterLink}
                    to="/products"
                    variant="text"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      color: style.color4,
                      fontWeight: 700,
                      px: 1.35,
                      py: 0.7,
                      borderRadius: "999px",
                      textTransform: "none",
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.10)",
                        color: style.color4,
                      },
                    }}
                  >
                    Browse all plugins
                  </Button>
                </Stack>
              </Stack>
            </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={isImagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(6, 9, 14, 0.96)",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 1, md: 1.5 }, position: "relative" }}>
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 2,
              backgroundColor: "rgba(0,0,0,0.34)",
              color: style.color4,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box
            sx={{
              minHeight: { xs: 320, md: 560 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={selectedImage?.URI}
              alt={selectedImage?.altText || productData?.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <LoginRegisterModal
        isOpen={isLoginRegisterModalOpen}
        close={closeModal(setLoginRegisterModalOpen)}
        loginComplete={loginCallback}
      />
    </>
  );
}



// import React, { useState, useContext, useEffect, useMemo } from "react";
// import axios from "axios";
// import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { toast } from "react-hot-toast";
// import ReactGA from "react-ga4";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import Seo from "../../components/Seo";
// import LoginRegisterModal from "../../components/LoginRegisterModal/LoginRegisterModal";
// import ProductSchemaMarkup from "./ProductSchemaMarkup";
// import { usePromotions } from "../../context/promotionsContext/promotionsContext";
// import {
//   Box,
//   Typography,
//   Button,
//   useMediaQuery,
//   Rating,
//   Divider,
//   Stack,
//   Paper,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
// import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
// import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
// import AppleIcon from "@mui/icons-material/Apple";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
// import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
// import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx"

// export default function ProductPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const { addToCart } = useContext(ShoppingCartContext);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//   const { sku } = useParams();
//   const navigate = useNavigate();

//   const [productData, setProductData] = useState(null);
//   const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//   const [macDownloadRequested, setMacDownloadRequested] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);
//   const { getProductDisplayPricing } = usePromotions();
//   const productPricing = useMemo(() => {
//     if (!productData || Number(productData.price) <= 0) return null;
//     return getProductDisplayPricing(productData);
//   }, [productData, getProductDisplayPricing]);
  
//   const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
//   const originalPrice = Number(productPricing?.originalPrice ?? productData?.price ?? 0);
//   const finalPrice = Number(productPricing?.finalPrice ?? productData?.price ?? 0);
//   const savingsPercent =
//     hasPromoDiscount && originalPrice > 0
//       ? Math.round((Number(productPricing.discountAmount) / originalPrice) * 100)
//       : 0;


//   useEffect(() => {
//     ReactGA.send({ hitType: "pageview", page: sku, title: sku });
//     getProductPageInfo();
//   }, [sku]);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     setSelectedImageIndex(0);
//   }, [productData?.productURI]);

//   useEffect(() => {
//     window.scrollTo({ top: 0, left: 0, behavior: "auto" });
//   }, [sku]);

//   const bulletPoints = useMemo(() => {
//     if (!productData) return [];
//     return [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);
//   }, [productData]);

//   const galleryImages = useMemo(() => {
//     if (!productData?.images) return [];
//     const valid = productData.images.filter((img) => img && img.URI);
//     if (valid.length <= 1) return valid;
//     return valid.slice(0, -1);
//   }, [productData]);

//   const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

//   const youtubeEmbedUrl = useMemo(() => {
//     const rawValue = productData?.youtubeVideo?.trim();
//     if (!rawValue) return "";

//     try {
//       const url = new URL(rawValue);
//       const hostname = url.hostname.replace(/^www\./, "");

//       if (hostname === "youtu.be") {
//         const videoId = url.pathname.split("/").filter(Boolean)[0];
//         return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
//       }

//       if (hostname.endsWith("youtube.com")) {
//         if (url.pathname.startsWith("/embed/")) {
//           return rawValue;
//         }

//         const pathSegments = url.pathname.split("/").filter(Boolean);
//         const videoId =
//           url.searchParams.get("v") ||
//           (pathSegments[0] === "shorts" ? pathSegments[1] : "");

//         if (!videoId) return "";

//         const startAt = url.searchParams.get("start") || url.searchParams.get("t");
//         const startSeconds = startAt ? startAt.replace(/s$/i, "") : "";

//         return `https://www.youtube.com/embed/${videoId}${
//           startSeconds ? `?start=${startSeconds}` : ""
//         }`;
//       }

//       return rawValue;
//     } catch (error) {
//       return `https://www.youtube.com/embed/${rawValue}`;
//     }
//   }, [productData?.youtubeVideo]);

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   const getProductPageInfo = async () => {
//     try {
//       const { data } = await axios.post("/get_product_page_info", { productURI: sku });
//       if (data) {
//         setProductData(data);
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     }
//   };

//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//     //toast.success(`${productData.name} added to cart`);
//   };

//   const downloadWindowsVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setWindowsDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.windowsDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const downloadMacVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setMacDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.macDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const navToSubscribePage = () => {
//     navigate("/subscribe");
//   };

//   const loginCallback = async () => {
//     const token = localStorage.getItem("token");
//     if (!token || !productData) return;

//     setLoginRegisterModalOpen(false);

//     try {
//       if (windowsDownloadRequested) {
//         setWindowsDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.windowsDownloadURI;
//       }

//       if (macDownloadRequested) {
//         setMacDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.macDownloadURI;
//       }
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const nextImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
//   };

//   const previousImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
//   };

//   if (!productData) {
//     return (
//       <>
//         <Seo
//           title="Parish Audio Plugin"
//           description="Professional audio plugins by Parish Audio."
//           canonical="https://parishaudio.com/products"
//         />
//         <Box sx={{ minHeight: height - 130 }}>
//           <LoadingSpinner />
//         </Box>
//       </>
//     );
//   }

//   const panelSx = {
//     borderRadius: "16px",
//     backgroundColor: style.darkPanel,
//     border: "1px solid rgba(255,255,255,0.08)",
//     boxShadow: "none",
//   };

//   const subtlePanelSx = {
//     borderRadius: "14px",
//     backgroundColor: style.darkerPanel,
//     border: "1px solid rgba(255,255,255,0.06)",
//     boxShadow: "none",
//   };

//   const videoDemoSection = youtubeEmbedUrl ? (
//     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//       <Stack spacing={1}>
//         <Stack direction="row" spacing={1} alignItems="center">
//           <InfoOutlinedIcon sx={{ color: style.color3 }} />
//           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//             Product Demo
//           </Typography>
//         </Stack>

//         <Box
//           sx={{
//             position: "relative",
//             width: "100%",
//             pt: "56.25%",
//             borderRadius: "12px",
//             overflow: "hidden",
//             backgroundColor: "rgba(0,0,0,0.18)",
//           }}
//         >
//           <Box
//             component="iframe"
//             src={youtubeEmbedUrl}
//             title={`${productData.name} product demo`}
//             allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
//             allowFullScreen
//             referrerPolicy="strict-origin-when-cross-origin"
//             sx={{
//               position: "absolute",
//               inset: 0,
//               width: "100%",
//               height: "100%",
//               border: 0,
//             }}
//           />
//         </Box>
//       </Stack>
//     </Paper>
//   ) : null;

//   return (
//     <>
//       <Seo
//         title={`${productData.name} VST3 Plugin | Parish Audio`}
//         description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
//         canonical={`https://parishaudio.com/products/${productData.productURI}`}
//         image={
//           productData?.images?.[0]?.URI
//             ? `https://parishaudio.com${productData.images[0].URI}`
//             : "https://parishaudio.com/assets/og-default.jpg"
//         }
//         imageAlt={
//           productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
//         }
//         ogType="product"
//       />

// <ProductSchemaMarkup
//   productData={productData}
//   productPricing={productPricing}
// />

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.5, md: 2.5 },
//           boxSizing: "border-box",
//         }}
//       >
//         <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
//         <SaleHeroBanner />
//         <br />
//           <Paper
//             elevation={0}
//             sx={{
//               width: "100%",
//               minWidth: 0,
//               borderRadius: "20px",
//               overflow: "hidden",
//               backgroundColor: style.color1,
//               border: `1px solid ${style.color2}`,
//               boxSizing: "border-box",
//             }}
//           >
//             <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//               {isMobile ? (
//                 <Stack spacing={1.5}>
//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Stack spacing={1}>
//                       <Chip
//                         label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                         sx={{
//                           alignSelf: "flex-start",
//                           borderRadius: "5px",
//                           color: style.color4,
//                           fontWeight: 700,
//                           backgroundColor:
//                             productData.price > 0
//                               ? "rgba(255,255,255,0.06)"
//                               : "rgba(0,180,60,0.18)",
//                           border:
//                             productData.price > 0
//                               ? "1px solid rgba(255,255,255,0.08)"
//                               : "1px solid rgba(0,200,80,0.35)",
//                         }}
//                       />

//                       <Typography
//                         variant="h1"
//                         sx={{
//                           fontSize: { xs: "2rem", md: "2.7rem" },
//                           lineHeight: 1.04,
//                           fontWeight: 800,
//                           color: style.color4,
//                           letterSpacing: "-0.03em",
//                         }}
//                       >
//                         {productData.name}
//                       </Typography>

//                       <Typography
//                         sx={{
//                           color: style.color3,
//                           fontSize: "1rem",
//                           lineHeight: 1.7,
//                         }}
//                       >
//                         {productData.shortDescription}
//                       </Typography>

//                       <Box
//                         component={RouterLink}
//                         to={`/reviews/${productData.productURI}`}
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 1,
//                           flexWrap: "wrap",
//                           width: "fit-content",
//                           textDecoration: "none",
//                           color: "inherit",
//                         }}
//                       >
//                         <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                         <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                           {productData.ratingCount > 0 ? productData.starRating : ""}
//                         </Typography>
//                         <Typography sx={{ color: style.color3 }}>
//                           ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                         </Typography>
//                       </Box>
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Box
//                       sx={{
//                         minHeight: 260,
//                         height: 260,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         backgroundColor: "rgba(0,0,0,0.18)",
//                         borderRadius: "12px",
//                         overflow: "hidden",
//                         position: "relative",
//                       }}
//                     >
//                       {galleryImages.length > 1 && (
//                         <>
//                           <IconButton
//                             onClick={previousImage}
//                             sx={{
//                               position: "absolute",
//                               left: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowBackIosNewRoundedIcon fontSize="small" />
//                           </IconButton>

//                           <IconButton
//                             onClick={nextImage}
//                             sx={{
//                               position: "absolute",
//                               right: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowForwardIosRoundedIcon fontSize="small" />
//                           </IconButton>
//                         </>
//                       )}

//                       <Box
//                         component="img"
//                         src={selectedImage?.URI}
//                         alt={selectedImage?.altText || productData.name}
//                         sx={{
//                           maxWidth: "100%",
//                           maxHeight: "100%",
//                           objectFit: "contain",
//                           display: "block",
//                           p: 1,
//                         }}
//                       />
//                     </Box>

//                     {galleryImages.length > 1 && (
//                       <Box
//                         sx={{
//                           mt: 1,
//                           display: "grid",
//                           gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                           gap: 1,
//                         }}
//                       >
//                         {galleryImages.map((image, index) => (
//                           <Box
//                             key={`${image.URI}-${index}`}
//                             onClick={() => setSelectedImageIndex(index)}
//                             sx={{
//                               height: 74,
//                               cursor: "pointer",
//                               borderRadius: "10px",
//                               overflow: "hidden",
//                               border:
//                                 index === selectedImageIndex
//                                   ? `2px solid ${style.color2}`
//                                   : "2px solid transparent",
//                               backgroundColor:
//                                 index === selectedImageIndex
//                                   ? "rgba(255,255,255,0.06)"
//                                   : "rgba(0,0,0,0.18)",
//                               "&:hover": { borderColor: style.color3 },
//                             }}
//                           >
//                             <Box
//                               component="img"
//                               src={image.URI}
//                               alt={image.altText || `${productData.name} preview ${index + 1}`}
//                               sx={{
//                                 width: "100%",
//                                 height: "100%",
//                                 objectFit: "contain",
//                                 display: "block",
//                                 p: 0.75,
//                               }}
//                             />
//                           </Box>
//                         ))}
//                       </Box>
//                     )}
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.5}>
//                       <Chip
//                         label="IN STOCK • INSTANT DOWNLOAD"
//                         size="small"
//                         sx={{
//                           alignSelf: "flex-start",
//                           color: style.color4,
//                           backgroundColor: "rgba(0,180,60,0.18)",
//                           border: "1px solid rgba(0,200,80,0.35)",
//                           fontWeight: 700,
//                           letterSpacing: 0.2,
//                         }}
//                       />

//                       {productData.price > 0 ? (
//                         <>
//                           <Typography sx={{ color: style.color3, fontWeight: 700 }}>
//                             Purchase this plugin
//                           </Typography>

//                           {/* <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                             <Typography
//                               sx={{
//                                 color: style.color3,
//                                 textDecoration: "line-through",
//                                 fontSize: "1.05rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                             <Typography
//                               sx={{
//                                 fontSize: "2rem",
//                                 fontWeight: 800,
//                                 color: style.color4,
//                                 lineHeight: 1,
//                               }}
//                             >
//                               ${Number(productData.price).toFixed(2)}
//                             </Typography>
//                           </Stack> */}

//                       <Stack spacing={0.8}>
//                         {hasPromoDiscount && (
//                           <Chip
//                             label={`SALE - Save ${savingsPercent}%`}
//                             size="small"
//                             sx={{
//                               alignSelf: "flex-start",
//                               color: "#111",
//                               backgroundColor: "#ffcc4d",
//                               border: "1px solid rgba(255,255,255,0.18)",
//                               fontWeight: 900,
//                               letterSpacing: 0.2,
//                             }}
//                           />
//                         )}

//                         <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                           <Typography
//                             sx={{
//                               color: style.color3,
//                               textDecoration: "line-through",
//                               fontSize: "1.05rem",
//                             }}
//                           >
//                             ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                           </Typography>

//                           {/* {hasPromoDiscount && Number(productData.msrp) > originalPrice && (
//                             <Typography
//                               sx={{
//                                 color: "rgba(255,255,255,0.42)",
//                                 textDecoration: "line-through",
//                                 fontSize: "0.95rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                           )} */}

//                           <Typography
//                             sx={{
//                               fontSize: "2rem",
//                               fontWeight: 900,
//                               color: style.color4,
//                               lineHeight: 1,
//                             }}
//                           >
//                             ${finalPrice.toFixed(2)}
//                           </Typography>
//                         </Stack>

//                         {hasPromoDiscount && (
//                           <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                             Sale price applied automatically at checkout.
//                           </Typography>
//                         )}
//                       </Stack>


//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             One-time purchase. Add this plugin to your cart and own it permanently.
//                           </Typography>

//                           <Button
//                             onClick={addProductToCart}
//                             startIcon={<ShoppingCartRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: style.accentGreen,
//                               "&:hover": {
//                                 backgroundColor: style.accentGreen,
//                                 filter: "brightness(1.05)",
//                               },
//                             }}
//                           >
//                             Add to Cart
//                           </Button>

//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={0.75}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                 Subscription
//                               </Typography>
//                             </Stack>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Try it free for 7 days with an all access subscription.
//                             </Typography>

//                             <Button
//                               href='/subscribe'
//                               startIcon={<BoltRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                               }}
//                             >
//                               Try FREE for 7 Days
//                             </Button>
//                           </Stack>
//                         </>
//                       ) : (
//                         <>
//                           <Typography
//                             sx={{
//                               fontWeight: 900,
//                               fontSize: "2rem",
//                               color: "rgb(30,170,60)",
//                               lineHeight: 1,
//                             }}
//                           >
//                             FREE
//                           </Typography>

//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             Download this VST3 plugin for Mac or Windows.
//                           </Typography>

//                           <Stack spacing={1}>
//                             {productData.windowsDownloadURI && (
//                               <Button
//                                 onClick={downloadWindowsVST}
//                                 startIcon={<WindowsRoundedIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                     filter: "brightness(1.05)",
//                                   },
//                                 }}
//                               >
//                                 Download for Windows
//                               </Button>
//                             )}

//                             {productData.macDownloadURI && (
//                               <Button
//                                 onClick={downloadMacVST}
//                                 startIcon={<AppleIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                     filter: "brightness(1.05)",
//                                   },
//                                 }}
//                               >
//                                 Download for Mac
//                               </Button>
//                             )}
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   {productData.audioURIs?.[0] && (
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Audio Demo
//                           </Typography>
//                         </Stack>
//                         <audio controls style={{ width: "100%", height: "36px" }}>
//                           <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                         </audio>
//                       </Stack>
//                     </Paper>
//                   )}

//                   {videoDemoSection}

// {productData.price > 0 && (
//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.25}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                           Download Information
//                         </Typography>
//                       </Stack>

//                       <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                         Availability: In stock. This product is delivered as an instant digital download.
//                       </Typography>

//                       {productData.price > 0 && (
//                         <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               Download the plugin installer for your device here.
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                             </Typography>
//                           </Stack>
//                         </Stack>
//                       </Paper>
//                       )}

//                       <Stack spacing={1}>
//                         {productData.windowsDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.windowsDownloadURI}
//                             startIcon={<WindowsRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Windows Download
//                           </Button>
//                         )}

//                         {productData.macDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.macDownloadURI}
//                             startIcon={<AppleIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Mac Download
//                           </Button>
//                         )}
//                       </Stack>
//                     </Stack>
//                   </Paper>
//                   )}
//                 </Stack>
//               ) : (
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: "minmax(360px, 0.92fr) minmax(0, 1.08fr)",
//                     gap: 2,
//                     alignItems: "start",
//                   }}
//                 >
//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Box
//                         sx={{
//                           minHeight: 420,
//                           height: 420,
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           backgroundColor: "rgba(0,0,0,0.18)",
//                           borderRadius: "12px",
//                           overflow: "hidden",
//                           position: "relative",
//                         }}
//                       >
//                         {galleryImages.length > 1 && (
//                           <>
//                             <IconButton
//                               onClick={previousImage}
//                               sx={{
//                                 position: "absolute",
//                                 left: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowBackIosNewRoundedIcon fontSize="small" />
//                             </IconButton>

//                             <IconButton
//                               onClick={nextImage}
//                               sx={{
//                                 position: "absolute",
//                                 right: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowForwardIosRoundedIcon fontSize="small" />
//                             </IconButton>
//                           </>
//                         )}

//                         <Box
//                           component="img"
//                           src={selectedImage?.URI}
//                           alt={selectedImage?.altText || productData.name}
//                           sx={{
//                             maxWidth: "100%",
//                             maxHeight: "100%",
//                             objectFit: "contain",
//                             display: "block",
//                             p: 1.5,
//                           }}
//                         />
//                       </Box>

//                       {galleryImages.length > 1 && (
//                         <Box
//                           sx={{
//                             mt: 1.25,
//                             display: "grid",
//                             gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                             gap: 1,
//                           }}
//                         >
//                           {galleryImages.map((image, index) => (
//                             <Box
//                               key={`${image.URI}-${index}`}
//                               onClick={() => setSelectedImageIndex(index)}
//                               sx={{
//                                 height: 82,
//                                 cursor: "pointer",
//                                 borderRadius: "10px",
//                                 overflow: "hidden",
//                                 border:
//                                   index === selectedImageIndex
//                                     ? `2px solid ${style.color2}`
//                                     : "2px solid transparent",
//                                 backgroundColor:
//                                   index === selectedImageIndex
//                                     ? "rgba(255,255,255,0.06)"
//                                     : "rgba(0,0,0,0.18)",
//                                 "&:hover": { borderColor: style.color3 },
//                               }}
//                             >
//                               <Box
//                                 component="img"
//                                 src={image.URI}
//                                 alt={image.altText || `${productData.name} preview ${index + 1}`}
//                                 sx={{
//                                   width: "100%",
//                                   height: "100%",
//                                   objectFit: "contain",
//                                   display: "block",
//                                   p: 0.75,
//                                 }}
//                               />
//                             </Box>
//                           ))}
//                         </Box>
//                       )}
//                     </Paper>

//                     {productData.audioURIs?.[0] && (
//                       <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="center">
//                             <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                             <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                               Audio Demo
//                             </Typography>
//                           </Stack>
//                           <audio controls style={{ width: "100%", height: "36px" }}>
//                             <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                           </audio>
//                         </Stack>
//                       </Paper>
//                     )}

//                     {videoDemoSection}

// {productData.price > 0 && (
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1.25}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Download Information
//                           </Typography>
//                         </Stack>

//                         <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                           Availability: In stock. This product is delivered as an instant digital download.
//                         </Typography>

//                         {productData.price > 0 && (
//                           <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                             <Stack spacing={1}>
//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   Download the plugin installer for your device here.
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                                 </Typography>
//                               </Stack>
//                             </Stack>
//                           </Paper>
//                         )}

//                         <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                           {productData.windowsDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.windowsDownloadURI}
//                               startIcon={<WindowsRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Windows Download
//                             </Button>
//                           )}

//                           {productData.macDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.macDownloadURI}
//                               startIcon={<AppleIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Mac Download
//                             </Button>
//                           )}
//                         </Stack>
//                       </Stack>
//                     </Paper>
//                     )}
//                   </Stack>

//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
//                       <Stack spacing={1}>
//                         <Chip
//                           label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                           sx={{
//                             alignSelf: "flex-start",
//                             borderRadius: "5px",
//                             color: style.color4,
//                             fontWeight: 700,
//                             backgroundColor:
//                               productData.price > 0
//                                 ? "rgba(255,255,255,0.06)"
//                                 : "rgba(0,180,60,0.18)",
//                             border:
//                               productData.price > 0
//                                 ? "1px solid rgba(255,255,255,0.08)"
//                                 : "1px solid rgba(0,200,80,0.35)",
//                           }}
//                         />

//                         <Typography
//                           variant="h1"
//                           sx={{
//                             fontSize: { xs: "2rem", md: "2.65rem" },
//                             lineHeight: 1.04,
//                             fontWeight: 800,
//                             color: style.color4,
//                             letterSpacing: "-0.03em",
//                           }}
//                         >
//                           {productData.name}
//                         </Typography>

//                         <Typography
//                           sx={{
//                             color: style.color3,
//                             fontSize: { xs: "1rem", md: "1.03rem" },
//                             lineHeight: 1.7,
//                           }}
//                         >
//                           {productData.shortDescription}
//                         </Typography>

//                         <Box
//                           component={RouterLink}
//                           to={`/reviews/${productData.productURI}`}
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 1,
//                             flexWrap: "wrap",
//                             width: "fit-content",
//                             textDecoration: "none",
//                             color: "inherit",
//                             pt: 0.25,
//                           }}
//                         >
//                           <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                           <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                             {productData.ratingCount > 0 ? productData.starRating : ""}
//                           </Typography>
//                           <Typography sx={{ color: style.color3 }}>
//                             ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                           </Typography>
//                         </Box>
//                       </Stack>

//                       <Divider sx={{ my: 1.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

//                       <Stack spacing={1.5}>
//                         <Chip
//                           label="IN STOCK • INSTANT DOWNLOAD"
//                           size="small"
//                           sx={{
//                             alignSelf: "flex-start",
//                             color: style.color4,
//                             backgroundColor: "rgba(0,180,60,0.18)",
//                             border: "1px solid rgba(0,200,80,0.35)",
//                             fontWeight: 700,
//                             letterSpacing: 0.2,
//                           }}
//                         />

//                         {productData.price > 0 ? (
//                           <>
//                             <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                               Purchase this plugin
//                             </Typography>
//                             <Stack spacing={0.8}>
//                                 {hasPromoDiscount && (
//                                   <Chip
//                                     label={`SALE - Save ${savingsPercent}%`}
//                                     size="small"
//                                     sx={{
//                                       alignSelf: "flex-start",
//                                       color: "#111",
//                                       backgroundColor: "#ffcc4d",
//                                       border: "1px solid rgba(255,255,255,0.18)",
//                                       fontWeight: 900,
//                                       letterSpacing: 0.2,
//                                     }}
//                                   />
//                                 )}

//                                 <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                                   <Typography
//                                     sx={{
//                                       color: style.color3,
//                                       textDecoration: "line-through",
//                                       fontSize: "1.05rem",
//                                     }}
//                                   >
//                                     ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                                   </Typography>

//                                   <Typography
//                                     sx={{
//                                       fontSize: "2rem",
//                                       fontWeight: 900,
//                                       color: style.color4,
//                                       lineHeight: 1,
//                                     }}
//                                   >
//                                     ${finalPrice.toFixed(2)}
//                                   </Typography>
//                                 </Stack>

//                                 {hasPromoDiscount && (
//                                   <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                                     Sale price applied automatically at checkout.
//                                   </Typography>
//                                 )}
//                               </Stack>

//                             {/* <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
//                               <Typography
//                                 sx={{
//                                   color: style.color3,
//                                   textDecoration: "line-through",
//                                   fontSize: "1.15rem",
//                                 }}
//                               >
//                                 ${Number(productData.msrp).toFixed(2)}
//                               </Typography>
//                               <Typography
//                                 sx={{
//                                   fontSize: "2.1rem",
//                                   fontWeight: 800,
//                                   color: style.color4,
//                                   lineHeight: 1,
//                                 }}
//                               >
//                                 ${Number(productData.price).toFixed(2)}
//                               </Typography>
//                             </Stack> */}

//                             <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
//                               One-time purchase. Add this plugin to your cart and own it permanently.
//                             </Typography>

//                             <Button
//                               onClick={addProductToCart}
//                               startIcon={<ShoppingCartRoundedIcon />}
//                               sx={{
//                                 alignSelf: "flex-start",
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: style.accentGreen,
//                                 px: 2.5,
//                                 "&:hover": {
//                                   backgroundColor: style.accentGreen,
//                                   filter: "brightness(1.05)",
//                                 },
//                               }}
//                             >
//                               Add to Cart
//                             </Button>

//                             <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                             <Stack spacing={0.8}>
//                               <Stack direction="row" spacing={1} alignItems="center">
//                                 <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                                 <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                   Subscription
//                                 </Typography>
//                               </Stack>

//                               <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                                 Try it free for 7 days with an all access subscription.
//                               </Typography>

//                               <Button
//                                 href='/subscribe'
//                                 startIcon={<BoltRoundedIcon />}
//                                 sx={{
//                                   alignSelf: "flex-start",
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   px: 2.5,
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                   },
//                                 }}
//                               >
//                                 Try FREE for 7 Days
//                               </Button>

//                               <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                                 $9.95 per month for access to all plugins. Cancel anytime.
//                               </Typography>
//                             </Stack>
//                           </>
//                         ) : (
//                           <>
//                             <Typography
//                               sx={{
//                                 fontWeight: 900,
//                                 fontSize: "2.2rem",
//                                 color: "rgb(30,170,60)",
//                                 lineHeight: 1,
//                               }}
//                             >
//                               FREE
//                             </Typography>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Download this VST3 plugin for Mac or Windows.
//                             </Typography>

//                             <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                               {productData.windowsDownloadURI && (
//                                 <Button
//                                   onClick={downloadWindowsVST}
//                                   startIcon={<WindowsRoundedIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: "rgba(255,255,255,0.05)",
//                                     border: "1px solid rgba(255,255,255,0.10)",
//                                     "&:hover": {
//                                       backgroundColor: "rgba(255,255,255,0.10)",
//                                       filter: "brightness(1.05)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Windows
//                                 </Button>
//                               )}

//                               {productData.macDownloadURI && (
//                                 <Button
//                                   onClick={downloadMacVST}
//                                   startIcon={<AppleIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: "rgba(255,255,255,0.05)",
//                                     border: "1px solid rgba(255,255,255,0.10)",
//                                     "&:hover": {
//                                       backgroundColor: "rgba(255,255,255,0.10)",
//                                       filter: "brightness(1.05)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Mac
//                                 </Button>
//                               )}
//                             </Stack>
//                           </>
//                         )}
//                       </Stack>
//                     </Paper>
//                   </Stack>
//                 </Box>
//               )}

//               <Box sx={{ mt: 1.5 }}>
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
//                     gap: 2,
//                     alignItems: "stretch",
//                   }}
//                 >
//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <LibraryMusicRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Overview
//                         </Typography>
//                       </Stack>

//                       <Typography
//                         sx={{
//                           color: style.color4,
//                           lineHeight: 1.75,
//                           whiteSpace: "pre-line",
//                           fontSize: "1rem",
//                         }}
//                       >
//                         {productData.longDescription}
//                       </Typography>

//                       {bulletPoints.length > 0 && (
//                         <>
//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={1}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <InfoOutlinedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                                 Highlights
//                               </Typography>
//                             </Stack>

//                             <Box
//                               sx={{
//                                 display: "grid",
//                                 gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
//                                 gap: 1.1,
//                               }}
//                             >
//                               {bulletPoints.map((point, idx) => (
//                                 <Paper
//                                   key={idx}
//                                   elevation={0}
//                                   sx={{
//                                     ...subtlePanelSx,
//                                     p: 1.15,
//                                     minHeight: 25,
//                                     display: "flex",
//                                     alignItems: "flex-start",
//                                   }}
//                                 >
//                                   <Stack direction="row" spacing={1} alignItems="flex-start">
//                                     <CheckCircleRoundedIcon
//                                       sx={{
//                                         color: style.color3,
//                                         mt: "2px",
//                                         fontSize: "1.05rem",
//                                         flexShrink: 0,
//                                       }}
//                                     />
//                                     <Typography
//                                       sx={{
//                                         color: style.color4,
//                                         lineHeight: 1.5,
//                                         fontSize: "0.92rem",
//                                         wordBreak: "break-word",
//                                       }}
//                                     >
//                                       {point}
//                                     </Typography>
//                                   </Stack>
//                                 </Paper>
//                               ))}
//                             </Box>
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <TuneRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Controls
//                         </Typography>
//                       </Stack>

//                       {productData.controls?.length > 0 ? (
//                         <Stack spacing={1.1}>
//                           {productData.controls.map((control, index) => (
//                             <Card
//                               key={index}
//                               elevation={0}
//                               sx={{
//                                 backgroundColor: "rgba(0,0,0,0.18)",
//                                 color: style.color4,
//                                 borderRadius: "12px",
//                                 border: "1px solid rgba(255,255,255,0.06)",
//                                 boxShadow: "none",
//                               }}
//                             >
//                               <CardContent sx={{ p: "14px !important" }}>
//                                 <Typography
//                                   sx={{
//                                     fontWeight: 800,
//                                     color: style.color4,
//                                     mb: 0.4,
//                                   }}
//                                 >
//                                   {control.name}
//                                 </Typography>
//                                 <Typography
//                                   sx={{
//                                     color: style.color3,
//                                     lineHeight: 1.55,
//                                     fontSize: "0.92rem",
//                                   }}
//                                 >
//                                   {control.description}
//                                 </Typography>
//                               </CardContent>
//                             </Card>
//                           ))}
//                         </Stack>
//                       ) : (
//                         <Typography sx={{ color: style.color3 }}>
//                           No control information available.
//                         </Typography>
//                       )}
//                     </Stack>
//                   </Paper>
//                 </Box>
//               </Box>

//               <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

//               <Stack
//                 direction={{ xs: "column", md: "row" }}
//                 spacing={1.5}
//                 justifyContent="space-between"
//                 alignItems={{ xs: "flex-start", md: "center" }}
//               >
//                 <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
//                   <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                     Format:
//                   </Typography>
//                   <Chip
//                     label="VST3"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Mac"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Windows"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                 </Stack>

//                 <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
//                   <Button
//                     component={RouterLink}
//                     to={`/reviews/${productData.productURI}`}
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Read all reviews
//                   </Button>

//                   <Button
//                     component={RouterLink}
//                     to="/products"
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Browse all plugins
//                   </Button>
//                 </Stack>
//               </Stack>
//             </Box>
//           </Paper>
//         </Box>
//       </Box>

//       <LoginRegisterModal
//         isOpen={isLoginRegisterModalOpen}
//         close={closeModal(setLoginRegisterModalOpen)}
//         loginComplete={loginCallback}
//       />
//     </>
//   );
// }
